import { useEffect, useMemo, useState } from "react";
import { useNotify } from "ra-core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import { useIsDueno } from "../useIsDueno";
import { useAllOffers } from "../useOffers";
import { ESTADOS } from "../directory/directoryFilters";

interface ZonaOfertas {
  estados: string[];
  municipios: string[]; // "Estado|Municipio"
  scian: string[];
  ofertas: string[]; // slugs
  tope_diario: number; // E6.6: promociones al CRM por día
}

interface Traslape {
  sales_id: number;
  nombre: string;
  zonas: string[];
}

const toList = (s: string) =>
  s
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

const toggle = <T,>(list: T[], x: T): T[] =>
  list.includes(x) ? list.filter((y) => y !== x) : [...list, x];

/**
 * E6.6b · Zona (estados y municipios) y ofertas de un freelancer. Solo la ve el dueño
 * (useIsDueno: mientras carga o si falla, no se muestra). La base vuelve a validar todo:
 * freelancer_set_zona y freelancer_set_ofertas rechazan a quien no es el dueño.
 * Un estado sin municipios marcados = el estado completo.
 * E6.6: también el tope diario de "Pasar al CRM" (freelancer_set_tope, solo el dueño).
 */
export const ZonaGiroCard = ({
  salesId,
  nombre,
}: {
  salesId: number | string;
  userId?: string;
  nombre?: string;
}) => {
  const sb = getSupabaseClient();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const isDueno = useIsDueno();
  const { data: offers } = useAllOffers();
  const [form, setForm] = useState<ZonaOfertas | null>(null);
  const [scian, setScian] = useState("");
  const [saving, setSaving] = useState(false);

  const { data: conf } = useQuery({
    queryKey: ["zona_ofertas", Number(salesId)],
    enabled: isDueno,
    retry: false,
    queryFn: async () => {
      const { data, error } = await sb.rpc("freelancer_get_zona_ofertas", {
        p_sales_id: Number(salesId),
      });
      // P0002: no es freelancer (la tarjeta no aplica)
      if (error) return null;
      return data as ZonaOfertas;
    },
  });

  useEffect(() => {
    if (!conf) return;
    setForm(conf);
    setScian(conf.scian.join(", "));
  }, [conf]);

  const { data: traslapes } = useQuery({
    queryKey: [
      "zona_traslapes",
      Number(salesId),
      form?.estados,
      form?.municipios,
    ],
    enabled: isDueno && !!form,
    queryFn: async () => {
      const { data, error } = await sb.rpc("freelancer_zona_traslapes", {
        p_sales_id: Number(salesId),
        p_estados: form!.estados,
        p_municipios: form!.municipios,
      });
      if (error) throw error;
      return (data ?? []) as Traslape[];
    },
  });

  if (!isDueno || !conf || !form) return null;

  const save = async () => {
    const quien = nombre ? ` de ${nombre}` : "";
    if (
      !window.confirm(
        `¿Guardar la zona y las ofertas${quien}? Lo verá en su siguiente consulta.`,
      )
    )
      return;
    setSaving(true);
    const zona = await sb.rpc("freelancer_set_zona", {
      p_sales_id: Number(salesId),
      p_estados: form.estados,
      p_municipios: form.municipios,
      p_scian_prefijos: toList(scian),
    });
    const ofertas = zona.error
      ? null
      : await sb.rpc("freelancer_set_ofertas", {
          p_sales_id: Number(salesId),
          p_slugs: form.ofertas,
        });
    const tope =
      zona.error || ofertas?.error
        ? null
        : await sb.rpc("freelancer_set_tope", {
            p_sales_id: Number(salesId),
            // vacío -> null: la base lo rechaza con su mensaje
            p_tope: Number.isFinite(form.tope_diario) ? form.tope_diario : null,
          });
    setSaving(false);
    queryClient.invalidateQueries({ queryKey: ["zona_ofertas"] });
    if (zona.error) {
      notify(`No se guardó nada: ${zona.error.message}`, { type: "error" });
      return;
    }
    if (ofertas?.error) {
      notify(
        `La zona se guardó, pero las ofertas no: ${ofertas.error.message}`,
        { type: "error" },
      );
      return;
    }
    if (tope?.error) {
      notify(
        `La zona y las ofertas se guardaron, pero el tope no: ${tope.error.message}`,
        { type: "error" },
      );
      return;
    }
    notify("Zona y ofertas guardadas", { type: "success" });
  };

  return (
    <Card className="mt-4 gap-4 p-4">
      <h2 className="flex items-center gap-2 text-base font-extrabold">
        <MapPin className="size-5 text-primary" />
        Zona y ofertas{nombre ? ` · ${nombre}` : ""}
      </h2>

      <section className="space-y-2">
        <h3 className="text-sm font-bold">Estados</h3>
        <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 md:grid-cols-4">
          {ESTADOS.map((e) => (
            <label key={e} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={form.estados.includes(e)}
                onCheckedChange={() =>
                  setForm({
                    ...form,
                    estados: toggle(form.estados, e),
                    // quitar un estado quita sus municipios
                    municipios: form.municipios.filter(
                      (m) =>
                        !m.startsWith(`${e}|`) || !form.estados.includes(e),
                    ),
                  })
                }
              />
              {e}
            </label>
          ))}
        </div>
        {form.estados.length === 0 && (
          <p className="text-sm text-destructive">
            Marca al menos un estado: sin zona el freelancer no ve ninguna
            empresa.
          </p>
        )}
      </section>

      {[...form.estados].sort().map((e) => (
        <MunicipiosEstado
          key={e}
          estado={e}
          selected={form.municipios}
          onToggle={(m) =>
            setForm({ ...form, municipios: toggle(form.municipios, m) })
          }
        />
      ))}

      <section className="space-y-2">
        <h3 className="text-sm font-bold">Ofertas</h3>
        <div className="grid gap-1.5 sm:grid-cols-2">
          {offers?.map((o) => (
            <label key={o.id} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={form.ofertas.includes(o.slug)}
                onCheckedChange={() =>
                  setForm({ ...form, ofertas: toggle(form.ofertas, o.slug) })
                }
              />
              {o.name}
              {!o.active && (
                <span className="text-xs text-muted-foreground">archivada</span>
              )}
            </label>
          ))}
        </div>
        {form.ofertas.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Sin ofertas marcadas no podrá abrir oportunidades.
          </p>
        )}
      </section>

      <div className="space-y-1">
        <Label htmlFor="zona-scian">Giros (prefijos SCIAN, opcional)</Label>
        <Input
          id="zona-scian"
          value={scian}
          placeholder="722, 46"
          onChange={(e) => setScian(e.target.value)}
        />
      </div>

      <div className="space-y-1">
        <Label htmlFor="zona-tope">
          Tope diario de promociones al CRM (hora de México)
        </Label>
        <Input
          id="zona-tope"
          type="number"
          min={0}
          max={1000}
          className="w-32"
          value={Number.isFinite(form.tope_diario) ? form.tope_diario : ""}
          onChange={(e) =>
            setForm({ ...form, tope_diario: e.target.valueAsNumber })
          }
        />
      </div>

      {!!traslapes?.length && (
        <div className="flex gap-2 rounded-md border border-amber-500/50 p-3 text-sm">
          <AlertTriangle className="size-5 shrink-0 text-amber-500" />
          <div>
            <b>Esta zona se traslapa con otro freelancer</b> (no se bloquea):
            <ul className="list-disc pl-5">
              {traslapes.map((t) => (
                <li key={t.sales_id}>
                  {t.nombre || `Freelancer ${t.sales_id}`}: {t.zonas.join(", ")}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={save}
          disabled={saving || form.estados.length === 0}
        >
          Guardar zona y ofertas
        </Button>
      </div>
    </Card>
  );
};

/** Municipios de un estado marcado, con buscador y conteo de empresas. Ninguno = todo el estado. */
const MunicipiosEstado = ({
  estado,
  selected,
  onToggle,
}: {
  estado: string;
  selected: string[];
  onToggle: (pair: string) => void;
}) => {
  const [q, setQ] = useState("");
  const { data } = useQuery({
    queryKey: ["municipios", estado],
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("directory_municipios")
        .select("municipio, empresas")
        .eq("estado", estado)
        .order("municipio");
      if (error) throw error;
      return (data ?? []) as { municipio: string; empresas: number }[];
    },
  });
  const marcados = selected.filter((m) => m.startsWith(`${estado}|`)).length;
  const visibles = useMemo(
    () =>
      (data ?? []).filter((m) =>
        m.municipio.toLowerCase().includes(q.trim().toLowerCase()),
      ),
    [data, q],
  );
  return (
    <section className="space-y-2 rounded-md border p-3">
      <h3 className="text-sm font-bold">
        Municipios de {estado}{" "}
        <span className="font-normal text-muted-foreground">
          {marcados ? `${marcados} marcados` : "todo el estado"}
        </span>
      </h3>
      <Input
        value={q}
        placeholder={`Buscar municipio de ${estado}`}
        aria-label={`Buscar municipio de ${estado}`}
        onChange={(e) => setQ(e.target.value)}
      />
      <div className="grid max-h-48 gap-1 overflow-y-auto sm:grid-cols-2">
        {visibles.map(({ municipio, empresas }) => {
          const pair = `${estado}|${municipio}`;
          return (
            <label key={pair} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={selected.includes(pair)}
                onCheckedChange={() => onToggle(pair)}
              />
              {municipio}
              <span className="text-xs text-muted-foreground">
                {empresas.toLocaleString("es-MX")}
              </span>
            </label>
          );
        })}
      </div>
    </section>
  );
};
